import { UnreachableError } from 'base/helpers';
import { z } from 'zod';
import { Clock } from './clock';

export const enum DeltaType {
  ATOM_REPLACE = 'ar',
  OPTIONAL_REPLACE = 'or',
  LIST_INSERT = 'li',
  LIST_ENTOMB = 'lt',
  LIST_RESTORE = 'lr',
  MAP_SET = 'ms',
}

export type DeltaBase = {
  /* OID which the delta applies to */
  for: string;
  /* Time at which the delta was created, as a sortable string */
  time: string;
};

export type AtomDelta<S> = DeltaBase & {
  type: DeltaType.ATOM_REPLACE;
  from: S;
  to: S;
};
export type OptionalDelta<S> = DeltaBase & {
  type: DeltaType.OPTIONAL_REPLACE;
  from?: S;
  to?: S;
};
export type ListDelta<S> = DeltaBase &
  (
    | {
        type: DeltaType.LIST_INSERT;
        afterId?: string;
        itemId: string;
        item: S;
      }
    | {
        type: DeltaType.LIST_ENTOMB;
        itemId: string;
      }
    | {
        type: DeltaType.LIST_RESTORE;
        itemId: string;
      }
  );
export type StringMapDelta<S> = DeltaBase & {
  type: DeltaType.MAP_SET;
  key: string;
  from?: S;
  to?: S;
};

export type Delta<S> =
  | AtomDelta<S>
  | OptionalDelta<S>
  | ListDelta<S>
  | StringMapDelta<S>;
export type AnyDelta = Delta<unknown>;

// By default zod makes all unknows optional, which is not what we want.
const customUnknownSchema = z.custom<unknown & { _hack: 1 }>(() => true);

export const anyDeltaSchema: z.ZodType<AnyDelta> = z
  .object({
    for: z.string(),
    time: z.string(),
  })
  .and(
    z.discriminatedUnion('type', [
      z.object({
        type: z.literal(DeltaType.ATOM_REPLACE),
        from: customUnknownSchema,
        to: customUnknownSchema,
      }),
      z.object({
        type: z.literal(DeltaType.OPTIONAL_REPLACE),
        from: z.unknown(),
        to: z.unknown(),
      }),
      z.object({
        type: z.literal(DeltaType.LIST_INSERT),
        afterId: z.string().optional(),
        itemId: z.string(),
        item: customUnknownSchema,
      }),
      z.object({
        type: z.literal(DeltaType.LIST_ENTOMB),
        itemId: z.string(),
      }),
      z.object({
        type: z.literal(DeltaType.LIST_RESTORE),
        itemId: z.string(),
      }),
      z.object({
        type: z.literal(DeltaType.MAP_SET),
        key: z.string(),
        from: z.unknown(),
        to: z.unknown(),
      }),
    ]),
  );

export function createRevertAll<S>(clock: Clock, deltas: readonly Delta<S>[]) {
  return [...deltas].reverse().map((d) => createRevert(clock.advance(), d));
}

export function createRevert<S>(time: string, delta: Delta<S>): Delta<S> {
  switch (delta.type) {
    case DeltaType.ATOM_REPLACE:
      return {
        for: delta.for,
        time,
        type: DeltaType.ATOM_REPLACE,
        to: delta.from,
        from: delta.to,
      };
    case DeltaType.OPTIONAL_REPLACE:
      return {
        for: delta.for,
        time,
        type: DeltaType.OPTIONAL_REPLACE,
        to: delta.from,
        from: delta.to,
      };
    case DeltaType.LIST_INSERT:
    case DeltaType.LIST_RESTORE:
      return {
        for: delta.for,
        time,
        type: DeltaType.LIST_ENTOMB,
        itemId: delta.itemId,
      };
    case DeltaType.LIST_ENTOMB:
      return {
        for: delta.for,
        time,
        type: DeltaType.LIST_RESTORE,
        itemId: delta.itemId,
      };
    case DeltaType.MAP_SET:
      return {
        for: delta.for,
        time,
        type: DeltaType.MAP_SET,
        key: delta.key,
        from: delta.to,
        to: delta.from,
      };
    default:
      throw new UnreachableError(delta);
  }
}

/**
 * Removes deltas which have been override by later deltas & leave no monotonic changes.
 *
 * E.g.
 *
 *   If you have 2 adjacent ATOM_REPLACE deltas, the first one is redundant.
 *
 *   If you have a LIST_INSERT followed by a LIST_ENTOMB, the LIST_INSERT is NOT redundant,
 *   because it adds a new entry to the list (monotonic change) even though it is
 *   currently entombed.
 **/
export function compressDeltas(
  deltas: readonly AnyDelta[],
): readonly AnyDelta[] {
  if (deltas.length <= 1) return deltas;

  const byKey = new Map<string, AnyDelta[]>();
  for (const d of deltas) {
    const key = compressionKey(d);
    const list = byKey.get(key) || [];
    list.push(d);
    byKey.set(key, list);
  }

  const result: AnyDelta[] = [];
  for (const list of byKey.values()) {
    result.push(...compressDeltasForKey(list));
  }
  return result;
}

function compressionKey(d: AnyDelta): string {
  switch (d.type) {
    case DeltaType.ATOM_REPLACE:
    case DeltaType.OPTIONAL_REPLACE:
      return `${d.type}|${d.for}`;
    case DeltaType.MAP_SET:
      return `${d.type}|${d.for}|${d.key}`;
    case DeltaType.LIST_ENTOMB:
    case DeltaType.LIST_RESTORE:
      return `lrt|${d.for}|${d.itemId}`;
    case DeltaType.LIST_INSERT:
      return ''; // always kept, can not be compressed
    default:
      throw new UnreachableError(d);
  }
}

function compressDeltasForKey(
  deltas: readonly AnyDelta[],
): readonly AnyDelta[] {
  if (deltas.length <= 1) return deltas;

  const example = deltas[0];
  if (
    example.type === DeltaType.ATOM_REPLACE ||
    example.type === DeltaType.OPTIONAL_REPLACE ||
    example.type === DeltaType.MAP_SET ||
    example.type === DeltaType.LIST_ENTOMB ||
    example.type === DeltaType.LIST_RESTORE
  ) {
    let maxTime = example.time;
    let maxDelta: AnyDelta = example;
    const expectedKey = compressionKey(example);
    for (const d of deltas) {
      if (compressionKey(d) !== expectedKey) {
        throw new Error(
          `Deltas do not have same key ${expectedKey} vs ${compressionKey(d)}`,
        );
      }
      if (d.time > maxTime) {
        maxTime = d.time;
        maxDelta = d;
      }
    }
    return [maxDelta];
  }

  return deltas;
}
